'use client';

import { useTranslations } from 'next-intl';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useDisplayTimeZone } from '@/contexts/DisplayTimeZoneContext';
import { useMounted } from '@/hooks/useMounted';
import { zoneOffsetAtSafe } from '@/lib/zoneOffsetAt';

/**
 * One line, once per public event page: "Times shown in Asia/Kuala_Lumpur
 * (GMT+8)". Rendered only after mount (the viewer's clock is unknown on the
 * server) and only when the pinned event zone's offset differs from the
 * browser's. Offsets are compared at `at` — the event's start — so a Sydney
 * viewer in July still gets the note for a December Brisbane event (same
 * offset today, an hour apart then); with no `at`, at the current instant.
 */
export function EventTimeZoneNote({ at }: { at?: string | null }) {
  const t = useTranslations('Event');
  const zone = useDisplayTimeZone();
  const mounted = useMounted();
  if (!zone || !mounted) return null;

  const parsed = at ? new Date(at) : null;
  const instant = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
  const pinned = zoneOffsetAtSafe(zone, instant);
  const viewerOffsetMin = -instant.getTimezoneOffset();
  if (pinned.offsetMin === viewerOffsetMin) return null;

  return (
    <p className="text-muted flex items-center justify-center gap-1.5 px-4 py-2 text-xs">
      <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{t('timezoneNote', { zone, abbrev: pinned.abbrev })}</span>
    </p>
  );
}
